import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useApolloClient} from '@apollo/client';
import {AnimatePresence, motion} from 'framer-motion';
import {StarFilled, StarOutlined} from '@ant-design/icons';
import classNames from 'classnames/bind';
import {Tooltip} from 'antd';
import Logo from '../logo/logo';
import CustomLink from '../customLink/customLink';
import {logout} from 'redux/store/auth';
import {ADMIN_TABS, NavTabsEnum} from 'utils/constants';
import {buildDashboardSearchIndex} from 'utils/dashboardSearchIndex';
import {useFavoritesStore} from '../../zustand/favoritesStore';
import {useTicketAlertsStore} from '../../zustand/ticketAlertsStore';
import {unregisterAdminPush} from 'utils/adminPush';
import {sectionsForTabs, unsectionedKeys} from './navSections';
import styles from './nav.module.scss';

const cx = classNames.bind(styles);

/**
 * The dashboard sidebar.
 *
 * Sixty destinations under twenty-four top-level entries, so the work here is
 * mostly about making that many things findable:
 *
 *  - Entries sit under section headings (navSections) rather than in one
 *    undifferentiated column.
 *  - Groups stay open across navigation and remember what was open. The old
 *    nav reset the open group on every pathname change, so clicking a sub-item
 *    collapsed the group you were working in — the single loudest complaint.
 *  - The group containing the current page opens itself, and is marked when
 *    collapsed, so you can always see where you are.
 *  - The whole rail collapses to icons, which gives dense tables back ~180px.
 *
 * What deliberately did NOT change: who can see what. Visibility still comes
 * from `extractTabs` + the same `availableTabs.includes(tab.tab)` test, and
 * the search palette, the favourites view and the stars are still
 * super-admin-only.
 */

const OPEN_GROUPS_KEY = 'navOpenGroups';

/** Reads the remembered open groups for one admin. */
const readOpenGroups = userId => {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return Array.isArray(parsed?.[userId]) ? parsed[userId] : [];
  } catch (error) {
    // A malformed entry must never take the nav down with it.
    return [];
  }
};

const writeOpenGroups = (userId, keys) => {
  if (!userId) return;
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    window.localStorage.setItem(
      OPEN_GROUPS_KEY,
      JSON.stringify({...parsed, [userId]: keys}),
    );
  } catch (error) {
    // Private mode / quota. Losing the preference is acceptable.
  }
};

/** Absolute path for a nav `to`, matching what CustomLink resolves to. */
const pathFor = to =>
  to === '' || to == null
    ? '/dashboard'
    : `/dashboard/${String(to).replace(/^\/+/, '')}`;

/** Whether a pathname is inside a nav destination. */
const isPathActive = (pathname, to) => {
  const target = pathFor(to);
  if (target === '/dashboard') return pathname === '/dashboard';
  return pathname === target || pathname.startsWith(`${target}/`);
};

/**
 * One navigable row, with the super-admin favourite star.
 *
 * In the collapsed rail the label is dropped and carried by a tooltip instead,
 * so nothing becomes unreachable — just narrower.
 */
const NavLeaf = ({
  icon,
  name,
  to,
  entryId,
  favApi,
  alwaysShowStar,
  compact,
  collapsed,
  badgeCount = 0,
}) => {
  const isFav = !!favApi?.favIds?.includes(entryId);
  // Stars are meaningless in the rail — there is no room and no favourites
  // view to send them to.
  const canFavorite = !!(favApi?.userId && entryId) && !collapsed;

  const row = (
    <div className={cx('navLeaf', collapsed ? 'navLeafRail' : '')}>
      <CustomLink
        icon={icon}
        name={name}
        to={to}
        compact={compact}
        badgeCount={badgeCount}
        // No width fits every label — "Recurring Investments" and "Workflow
        // Orchestrator" clip at any sidebar width worth having — so the full
        // text is always one hover away rather than lost.
        title={name}
        className={cx(
          canFavorite ? 'leafLink' : '',
          collapsed ? 'railLink' : '',
        )}
      />
      {canFavorite && (
        <button
          type="button"
          aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
          className={cx(
            'leafStar',
            alwaysShowStar ? 'leafStarShown' : '',
            isFav ? 'leafStarActive' : '',
          )}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            favApi.toggleFavorite(favApi.userId, entryId);
          }}>
          {isFav ? <StarFilled /> : <StarOutlined />}
        </button>
      )}
    </div>
  );

  return collapsed ? (
    <Tooltip title={name} placement="right" mouseEnterDelay={0.15}>
      {row}
    </Tooltip>
  ) : (
    row
  );
};

/**
 * A collapsible group of sub-tabs.
 *
 * Expanded rail: a disclosure row plus an indented list. Collapsed rail: the
 * icon opens the children in a hover flyout, so a group is still one hover
 * from its contents rather than requiring the sidebar to be re-expanded.
 */
const NavGroup = ({
  name,
  icon,
  children,
  isOpen,
  onToggle,
  hasActiveChild,
  collapsed,
}) => {
  /**
   * Where the rail flyout is drawn, or null when closed.
   *
   * It has to be `position: fixed` with measured coordinates rather than
   * absolute inside the group: the icon list scrolls, and an `overflow` that
   * scrolls also clips, so an absolutely-positioned panel was rendered with
   * the right geometry and then cropped away to nothing at the rail's edge.
   *
   * Opening on click as well as hover keeps the children reachable by
   * keyboard — a `visibility: hidden` panel takes no focus, so tabbing
   * otherwise skips straight past the whole group.
   */
  const [flyout, setFlyout] = useState(null);
  const anchorRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openFlyout = () => {
    cancelClose();
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setFlyout({top: rect.top - 8, left: rect.right + 12});
  };

  const closeFlyout = () => {
    cancelClose();
    setFlyout(null);
  };

  /**
   * Closing is deferred, not immediate.
   *
   * The panel is `position: fixed`, so although it is a DOM child of the
   * group, it sits beside the group's 44px box rather than inside it. Reaching
   * it means crossing dead space, which fires `mouseleave` on the group and
   * shut the panel before the pointer ever arrived — the sub-tabs were
   * visible but unclickable. The grace period, plus the panel cancelling the
   * timer when the pointer lands on it, makes that trip survivable.
   */
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setFlyout(null), 180);
  };

  useEffect(() => cancelClose, []);

  // Keep a flyout anchored near the bottom of a tall rail on screen.
  useLayoutEffect(() => {
    if (!flyout || !flyoutRef.current) return;
    const height = flyoutRef.current.offsetHeight;
    const maxTop = window.innerHeight - height - 12;
    if (flyout.top > maxTop)
      setFlyout(current => ({...current, top: Math.max(12, maxTop)}));
  }, [flyout]);

  if (collapsed)
    return (
      <div
        ref={anchorRef}
        className={cx('railGroup', hasActiveChild ? 'railGroupOn' : '')}
        onMouseEnter={openFlyout}
        onMouseLeave={scheduleClose}>
        <button
          type="button"
          className={styles.railGroupButton}
          aria-label={name}
          aria-expanded={Boolean(flyout)}
          onClick={() => (flyout ? closeFlyout() : openFlyout())}
          onKeyDown={e => {
            if (e.key === 'Escape') closeFlyout();
          }}>
          {icon}
        </button>
        {flyout && (
          <div
            ref={flyoutRef}
            className={styles.railFlyout}
            style={{top: flyout.top, left: flyout.left}}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onClick={closeFlyout}
            onKeyDown={e => {
              if (e.key === 'Escape') closeFlyout();
            }}>
            <span className={styles.railFlyoutTitle}>{name}</span>
            {children}
          </div>
        )}
      </div>
    );

  return (
    <div className={styles.group}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={cx(
          'groupButton',
          isOpen ? 'groupOpen' : '',
          // A collapsed group holding the current page still says so, so the
          // sidebar never loses track of where you are.
          hasActiveChild && !isOpen ? 'groupHasActive' : '',
        )}>
        {!!icon && icon}
        <span className={styles.groupName} title={name}>
          {name}
        </span>
        {hasActiveChild && !isOpen && <span className={styles.groupDot} />}
        <ChevronIcon className={cx('chevron', isOpen ? 'chevronOpen' : '')} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className={styles.groupItems}
            initial={{height: 0, opacity: 0}}
            animate={{height: 'auto', opacity: 1}}
            exit={{height: 0, opacity: 0}}
            transition={{duration: 0.2, ease: 'easeOut'}}>
            <div className={styles.groupItemsInner}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Nav = ({isOpen, collapsed = false, onToggleCollapse, className}) => {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const location = useLocation();
  const role = useSelector(state => state?.auth?.userData?.user);
  const userId = useSelector(state => state?.auth?.userData?.user?.id);
  const roleName = role?.activeAdminTab?.name;
  const isSuperAdmin = roleName === 'SUPERADMIN';

  // New-ticket count for the Customer Support badge (0 = no badge). Super
  // admins don't get ticket alerts, and the count is persisted, so a stale one
  // from before that change would otherwise sit there forever.
  const unseen = useTicketAlertsStore(state => state.unseen);
  const supportUnseen = isSuperAdmin ? 0 : unseen;
  const navBadges = useMemo(
    () => ({[NavTabsEnum.CustomerSupport]: supportUnseen}),
    [supportUnseen],
  );

  const favoritesByUser = useFavoritesStore(state => state.favoritesByUser);
  const toggleFavorite = useFavoritesStore(state => state.toggleFavorite);
  const favIds = useMemo(
    () => favoritesByUser[userId] || [],
    [favoritesByUser, userId],
  );
  const searchIndex = useMemo(() => buildDashboardSearchIndex(), []);
  const favEntries = useMemo(
    () =>
      favIds
        .map(id => searchIndex.find(entry => entry.id === id))
        .filter(Boolean),
    [favIds, searchIndex],
  );

  // Star controls are super-admin only, unchanged.
  const favApi = isSuperAdmin ? {userId, favIds, toggleFavorite} : null;

  const [favView, setFavView] = useState(false);
  const showingFavorites = isSuperAdmin && favView;

  const [openGroups, setOpenGroups] = useState([]);
  // Guards the write below. Without it the initial empty state is persisted
  // before the stored value has been read, wiping the preference on load.
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Adopt the remembered set once the admin is known.
  useEffect(() => {
    setOpenGroups(readOpenGroups(userId));
    setGroupsLoaded(true);
  }, [userId]);

  // Persist from an effect, never from inside the state updater: React invokes
  // updaters more than once (StrictMode, and again when re-rendering from a
  // stale base), so a localStorage write in there fires at unpredictable times
  // with unpredictable values.
  useEffect(() => {
    if (!groupsLoaded) return;
    writeOpenGroups(userId, openGroups);
  }, [openGroups, groupsLoaded, userId]);

  const toggleGroup = key =>
    setOpenGroups(previous =>
      previous.includes(key)
        ? previous.filter(item => item !== key)
        : [...previous, key],
    );

  const handleLogout = async () => {
    // Before the token goes, or the next agent here keeps getting these alerts.
    await unregisterAdminPush(apolloClient);
    // The awaiting-reply count is per agent: don't diff the next one against it.
    useTicketAlertsStore.getState().reset();
    dispatch(logout());
    navigation('/');
  };

  /**
   * Which tab keys this admin may see.
   *
   * Unchanged from the previous nav, quirks included: super admins get every
   * key walked out of the tree — which is why a sub-tab with no `tab` field at
   * all (Savings → Products) pushes `undefined` and therefore stays visible to
   * them and hidden from everyone else. Reproducing that exactly is
   * deliberate; changing who sees what is not this screen's job.
   */
  const extractTabs = permissions => {
    let availableTabs = [];

    const extractInnerTabs = tabs => {
      Object.keys(tabs).forEach(key => {
        const tab = tabs[key];
        availableTabs.push(tab.tab);
        if (tab.inner) {
          extractInnerTabs(tab.inner);
        }
      });
    };

    if (['SUPERADMIN', 'ADMIN'].includes(role?.activeAdminTab?.name)) {
      extractInnerTabs(ADMIN_TABS);
    } else {
      permissions.tabs.forEach(permission => {
        if (permission.tab) {
          availableTabs.push(permission.tab);
        }
      });
    }

    return availableTabs;
  };

  const availableTabs = role?.activeAdminTab
    ? extractTabs(role.activeAdminTab)
    : [];

  /** Sections, already filtered down to what this admin can reach. */
  const sections = useMemo(() => {
    const visible = {};
    Object.keys(ADMIN_TABS).forEach(key => {
      const tab = ADMIN_TABS[key];
      if (tab?.hideOnProd) return;
      if (tab.inner) {
        const innerTabs = Object.keys(tab.inner)
          .map(innerKey => ({innerKey, ...tab.inner[innerKey]}))
          .filter(innerTab => availableTabs.includes(innerTab.tab))
          .filter(
            innerTab => !innerTab.superAdminOnly || roleName === 'SUPERADMIN',
          )
          .filter(
            innerTab =>
              !innerTab.onlyRoles || innerTab.onlyRoles.includes(roleName),
          );
        // A group with nothing reachable inside it is not rendered at all.
        if (!innerTabs.length) return;
        visible[key] = {...tab, innerTabs};
        return;
      }

      if (availableTabs.includes(tab.tab)) visible[key] = tab;
    });

    return sectionsForTabs(visible);
  }, [availableTabs.join('|'), roleName]);

  // A tab added to ADMIN_TABS but not to a section still renders (under
  // "More"); this says so out loud while developing rather than leaving it to
  // be noticed in production.
  useEffect(() => {
    const orphans = unsectionedKeys(ADMIN_TABS);
    if (orphans.length && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        `[nav] not assigned to a section, shown under "More": ${orphans.join(
          ', ',
        )}`,
      );
    }
  }, []);

  /** The group that holds the current page, so it can open itself. */
  const activeGroupKey = useMemo(() => {
    let found = null;
    sections.forEach(section =>
      section.entries.forEach(({key, tab}) => {
        if (!tab.innerTabs) return;
        if (
          tab.innerTabs.some(inner => isPathActive(location.pathname, inner.to))
        )
          found = key;
      }),
    );
    return found;
  }, [sections, location.pathname]);

  // Open the group you have navigated into — but never close anything, which
  // is what the old nav did on every single pathname change.
  useEffect(() => {
    if (!activeGroupKey) return;
    setOpenGroups(previous =>
      previous.includes(activeGroupKey)
        ? previous
        : [...previous, activeGroupKey],
    );
  }, [activeGroupKey]);

  if (!role?.activeAdminTab)
    return (
      <nav className={cx('nav', className, isOpen ? 'show' : '')} id="navbar">
        <div className={styles.noAccess}>
          <h2>No tab access granted</h2>
          <p>Check that this account is an admin, then sign in again.</p>
        </div>
        <button className={styles.logout} onClick={() => navigation('/')}>
          Log out
        </button>
      </nav>
    );

  const renderEntry = ({key, tab}) => {
    if (tab.innerTabs)
      return (
        <NavGroup
          key={key}
          icon={tab.icon}
          name={tab.name}
          collapsed={collapsed}
          isOpen={openGroups.includes(key)}
          hasActiveChild={activeGroupKey === key}
          onToggle={() => toggleGroup(key)}>
          {tab.innerTabs.map(innerTab => (
            <NavLeaf
              key={innerTab.name}
              icon={innerTab.icon}
              name={innerTab.name}
              to={innerTab.to}
              entryId={`${key}:${innerTab.innerKey}`}
              favApi={favApi}
              compact
              // A group's badge lives on the child it belongs to — the new
              // ticket count on Tickets, not on Performance.
              badgeCount={innerTab.noBadge ? 0 : navBadges?.[innerTab.tab] || 0}
            />
          ))}
        </NavGroup>
      );

    return (
      <NavLeaf
        key={key}
        icon={tab.icon}
        name={tab.name}
        to={tab.to}
        entryId={key}
        favApi={favApi}
        collapsed={collapsed}
        badgeCount={navBadges?.[tab.tab] || 0}
      />
    );
  };

  return (
    <nav
      id="navbar"
      className={cx(
        'nav',
        className,
        isOpen ? 'show' : '',
        collapsed ? 'railed' : '',
      )}>
      <div className={styles.logo}>
        <Logo onClick={() => navigation('')} />
      </div>

      {/* Collapsing the sidebar used to take the favourites view with it —
          the toggle was hidden and `showingFavorites` was forced off, so a
          pinned list became unreachable at exactly the width where a short
          list is most useful. In the rail the same switch is two icons. */}
      {isSuperAdmin && collapsed && (
        <div className={styles.railToggle}>
          <Tooltip title="All tabs" placement="right">
            <button
              type="button"
              aria-label="All tabs"
              aria-pressed={!favView}
              className={cx('railToggleBtn', !favView ? 'railToggleOn' : '')}
              onClick={() => setFavView(false)}>
              <GridIcon />
            </button>
          </Tooltip>
          <Tooltip title="Favorites" placement="right">
            <button
              type="button"
              aria-label="Favorites"
              aria-pressed={favView}
              className={cx('railToggleBtn', favView ? 'railToggleOn' : '')}
              onClick={() => setFavView(true)}>
              <StarFilled />
            </button>
          </Tooltip>
        </div>
      )}

      {isSuperAdmin && !collapsed && (
        <div className={styles.viewToggle}>
          <button
            type="button"
            className={cx('toggleBtn', !favView ? 'toggleActive' : '')}
            onClick={() => setFavView(false)}>
            {!favView && (
              <motion.span
                layoutId="navToggleIndicator"
                className={styles.toggleIndicator}
                transition={{type: 'spring', stiffness: 500, damping: 40}}
              />
            )}
            <span className={styles.toggleLabel}>All tabs</span>
          </button>
          <button
            type="button"
            className={cx('toggleBtn', favView ? 'toggleActive' : '')}
            onClick={() => setFavView(true)}>
            {favView && (
              <motion.span
                layoutId="navToggleIndicator"
                className={styles.toggleIndicator}
                transition={{type: 'spring', stiffness: 500, damping: 40}}
              />
            )}
            <span className={styles.toggleLabel}>
              <StarFilled className={styles.toggleStar} /> Favorites
            </span>
          </button>
        </div>
      )}

      <div className={styles.scroll}>
        {showingFavorites ? (
          <div className={cx('navLinks', 'favLinks')}>
            {favEntries.length === 0 ? (
              <div className={cx('emptyFav', collapsed ? 'emptyFavRail' : '')}>
                <StarFilled className={styles.emptyFavIcon} />
                {!collapsed && (
                  <>
                    <p>No favorites yet</p>
                    <span>
                      Star a tab from the search bar to pin it here for quick
                      access.
                    </span>
                  </>
                )}
              </div>
            ) : (
              favEntries.map(entry => (
                <NavLeaf
                  key={entry.id}
                  icon={entry.icon}
                  name={entry.label}
                  to={entry.to}
                  entryId={entry.id}
                  favApi={favApi}
                  collapsed={collapsed}
                  alwaysShowStar={!collapsed}
                />
              ))
            )}
          </div>
        ) : (
          <div className={styles.navLinks}>
            {sections.map(section => (
              <div key={section.id} className={styles.section}>
                {section.label &&
                  (collapsed ? (
                    <span className={styles.sectionRule} />
                  ) : (
                    <span className={styles.sectionLabel}>{section.label}</span>
                  ))}
                {section.entries.map(renderEntry)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <ChevronIcon
            className={cx('collapseIcon', collapsed ? 'collapseIconOn' : '')}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
        <button
          onClick={handleLogout}
          className={cx('logout', collapsed ? 'logoutRail' : '')}
          title="Log out">
          {collapsed ? <PowerIcon /> : 'Log out'}
        </button>
      </div>
    </nav>
  );
};

const GridIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true">
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <rect x="13" y="3" width="8" height="8" rx="2" />
    <rect x="3" y="13" width="8" height="8" rx="2" />
    <rect x="13" y="13" width="8" height="8" rx="2" />
  </svg>
);

const ChevronIcon = ({className}) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PowerIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M12 3v9M6.3 6.3a8 8 0 1 0 11.4 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export {Nav};
